import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { imageUrls } from "../lib/supabase";

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    // Handle scrolling to anchors when the page loads
    if (location.hash) {
      const id = location.hash.substring(1); // remove the # character
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
        }
      }, 0);
    }
  }, [location]);

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isMobileMenuOpen && !event.target.closest('.navbar-container')) {
        setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isMobileMenuOpen]);

  // Close mobile menu on window resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 992) {
        setIsMobileMenuOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleAnchorClick = (e, anchor) => {
    e.preventDefault();
    setIsMobileMenuOpen(false);

    // Check if we're not on the home page
    if (location.pathname !== "/") {
      // Store the target section in sessionStorage instead of using query params
      sessionStorage.setItem('scrollToSection', anchor.substring(1));
      // Navigate to home page without query parameters
      navigate("/");
    } else {
      // We're already on the home page, just scroll to the anchor
      const element = document.getElementById(anchor.substring(1));
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }
  };

  const handleNavLinkClick = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <nav className={`modern-navbar ${isScrolled ? "navbar-scrolled" : ""}`}>
      <div className="navbar-container">
        {/* Logo Section */}
        <Link className="navbar-brand" to="/" onClick={handleNavLinkClick}>
          <img
            src={imageUrls.logo()}
            alt="Ali Cranta Medical Logo"
            className="navbar-logo"
            loading="lazy"
          />
        </Link>

        {/* Mobile Menu Toggle */}
        <button
          className={`mobile-menu-toggle ${isMobileMenuOpen ? "active" : ""}`}
          type="button"
          onClick={toggleMobileMenu}
          aria-controls="navbar-menu"
          aria-expanded={isMobileMenuOpen}
          aria-label="Toggle navigation menu"
        >
          <div className="hamburger-lines">
            <span className="line line1"></span>
            <span className="line line2"></span>
            <span className="line line3"></span>
          </div>
        </button>

        {/* Navigation Menu */}
        <div
          className={`navbar-menu ${isMobileMenuOpen ? "menu-open" : ""}`}
          id="navbar-menu"
        >
          {/* Mobile Menu Overlay */}
          {isMobileMenuOpen && (
            <div 
              className="mobile-menu-overlay"
              onClick={() => setIsMobileMenuOpen(false)}
            ></div>
          )}

          <ul className="nav-list">
            <li className="nav-item">
              <Link
                to="/"
                className={`nav-link ${location.pathname === "/" ? "active" : ""}`}
                onClick={handleNavLinkClick}
              >
                <span className="nav-text">Acasa</span>
              </Link>
            </li>
            
            <li className="nav-item">
              <a
                className="nav-link"
                href="#about"
                onClick={(e) => handleAnchorClick(e, "#about")}
              >
                <span className="nav-text">Despre</span>
              </a>
            </li>
            
            <li className="nav-item">
              <a
                className="nav-link"
                href="#services-wrapper"
                onClick={(e) => handleAnchorClick(e, "#services-wrapper")}
              >
                <span className="nav-text">Servicii</span>
              </a>
            </li>
            
            <li className="nav-item">
              <Link
                to="/cariere"
                className={`nav-link ${location.pathname === "/cariere" ? "active" : ""}`}
                onClick={handleNavLinkClick}
              >
                <span className="nav-text">Cariere</span>
              </Link>
            </li>
            
            <li className="nav-item">
              <a
                className="nav-link"
                href="#our-team"
                onClick={(e) => handleAnchorClick(e, "#our-team")}
              >
                <span className="nav-text">Echipa</span>
              </a>
            </li>
            
            <li className="nav-item">
              <a
                className="nav-link external-link"
                href="https://vindecareprinterapiatheta.ro/"
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleNavLinkClick}
              >
                <span className="nav-text">Terapie Theta</span>
              </a>
            </li>
            
            <li className="nav-item">
              <Link
                to="/articole"
                className={`nav-link ${location.pathname === "/articole" ? "active" : ""}`}
                onClick={handleNavLinkClick}
              >
                <span className="nav-text">Articole</span>
              </Link>
            </li>
            
            <li className="nav-item">
              <Link
                to="/prețuri"
                className={`nav-link ${location.pathname === "/prețuri" ? "active" : ""}`}
                onClick={handleNavLinkClick}
              >
                <span className="nav-text">Prețuri</span>
              </Link>
            </li>
            
            <li className="nav-item">
              <a
                className="nav-link"
                href="#contact"
                onClick={(e) => handleAnchorClick(e, "#contact")}
              >
                <span className="nav-text">Contact</span>
              </a>
            </li>
            
            <li className="nav-item">
              <Link
                to="/faq"
                className={`nav-link ${location.pathname === "/faq" ? "active" : ""}`}
                onClick={handleNavLinkClick}
              >
                <span className="nav-text">FAQ</span>
              </Link>
            </li>
            
            <li className="nav-item cta-item">
              <Link
                to="/rezervări"
                className="cta-button"
                onClick={handleNavLinkClick}
              >
                <svg className="cta-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M8 7V3C8 2.46957 8.21071 1.96086 8.58579 1.58579C8.96086 1.21071 9.46957 1 10 1H14C14.5304 1 15.0391 1.21071 15.4142 1.58579C15.7893 1.96086 16 2.46957 16 3V7M8 7H16M8 7H6C5.46957 7 4.96086 7.21071 4.58579 7.58579C4.21071 7.96086 4 8.46957 4 9V19C4 19.5304 4.21071 20.0391 4.58579 20.4142C4.96086 20.7893 5.46957 21 6 21H18C18.5304 21 19.0391 20.7893 19.4142 20.4142C19.7893 20.0391 20 19.5304 20 19V9C20 8.46957 19.7893 7.96086 19.4142 7.58579C19.0391 7.21071 18.5304 7 18 7H16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span className="cta-text">Programare online</span>
              </Link>
            </li>
          </ul>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
